<template>
  <div
    :class="['form-element', hidden && 'd-none']"
    :data-test="dataTest"
  >
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty: true,
        error,
        id
      }"
    />
    <div :class="{ 'border rounded-5': !disabled }">
      <div class="d-flex">
        <p v-if="disabled && !valueFormated.length">—</p>
        <AppButton
          v-if="!(disabled || isUploading)"
          @click="openFileDialog"
        >
          <AppIcon
            name="attachment"
            class="text-primary"
          />
          Add Files
        </AppButton>
        <AppButton
          v-if="!!valueFormated.length && !isUploading"
          class="ml-3"
          @click="setModalState(true)"
        >
          {{ viewFilesText }}
        </AppButton>
        <AppSpinner
          v-if="isUploading"
          variant="primary"
        />
        <input
          :id="id"
          ref="fileInput"
          type="file"
          class="d-none"
          multiple
          :disabled="disabled"
          @change="addFile"
        />
      </div>
    </div>
    <AppModal
      v-if="isModalOpen"
      :useDimness="true"
      @close="setModalState(false)"
    >
      <template slot="modal-header">
        <h3 class="modal-title">
          Uploaded Files
        </h3>
      </template>
      <template slot="modal-body">
        <ul class="row list-unstyled mb-0">
          <li
            v-for="file of valueFormated"
            :key="file.path"
            class="col col-12 mt-2"
          >
            <div class="d-flex justify-content-between align-items-center bg-light-gray rounded-5 py-2 px-3">
              <strong class="text-truncate">{{ file.name }}</strong>
              <div class="d-flex ml-3">
                <FormYesNo
                  v-if="!disabled && allowFilePrivacyToggle"
                  class="d-flex mr-2 file-privacy"
                  label="Public file"
                  :value="!!file.public"
                  @input="toggleFilePrivacy(file)"
                />
                <AppButton
                  v-if="!disabled"
                  class="bmd-btn-icon"
                  @click="confirmDeleteFile(file)"
                >
                  <AppIcon
                    name="delete_forever"
                    class="text-danger"
                  />
                </AppButton>
                <AppButton
                  class="bmd-btn-icon ml-2"
                  @click="downloadFile(file)"
                >
                  <AppIcon
                    name="cloud_download"
                    class="text-primary"
                  />
                </AppButton>
              </div>
            </div>
          </li>
        </ul>
      </template>
      <template slot="modal-footer">
        <AppButton
          variant="secondary"
          @click="setModalState(false)"
        >
          Close
        </AppButton>
        <AppButton
          v-if="!disabled"
          raised
          @click="openFileDialog"
        >
          Add more
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { postSignedUrl, uploadToS3 } from '@/api'
import FormMixin from './FormMixin'
import FormLabel from './FormLabel'
import FormYesNo from '@/components/Forms/FormYesNo'

const FILE_UPLOADS_PUBLIC_BUCKET_NAME = 'coursedog-static-public'

// TODO: This component should accept an uploadProvider (like FormUploadImage) instead of handling it internally!

// Why we show the files in modal: https://github.com/nicholasdiao/coursedogv3/pull/1128#issuecomment-540057647

export default {
  mixins: [ FormMixin ],
  components: { FormLabel, FormYesNo },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    allowFilePrivacyToggle: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({ isUploading: false, isModalOpen: false }),
  computed: {
    ...mapGetters([ 'school' ]),
    viewFilesText () {
      return `View ${this.valueFormated.length} file${this.valueFormated.length > 1 ? 's' : ''}`
    },
    valueFormated () {
      return this.value || []
    },
  },
  methods: {
    async addFile () {
      this.isUploading = true
      await Promise.all([ ...this.$refs.fileInput.files ].map(async file => {
        try {
          // used generateToken for better and unique urls else filename can has some weird chars that could cause issue
          const fileExt = file.name.split('.').pop()
          // files will the main path then we can have sub paths like form, email, profile etc
          const fileKey = `files/form/${Date.now()}/${this.generateToken(12)}.${fileExt}`
          await uploadToS3({
            school: this.school,
            file,
            fileKey,
          })
          this.$emit('input', [ ...this.valueFormated, { name: file.name, path: fileKey } ])
          this.setModalState(true)
        } catch (error) {
          alert(`Something went wrong for file ${file.name}, please try another file`)
        }
      }))
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.isUploading = false
    },
    async downloadFile (file) {
      const bucketName = file.bucketName ? { bucketName: file.bucketName }
        : file.public ? { bucketName: FILE_UPLOADS_PUBLIC_BUCKET_NAME } : {}

      const url = await postSignedUrl({
        school: this.school,
        fileName: file.path,
        ...bucketName,
        type: 'get',
        originalName: file.name,
      })
      window.open(url, '_blank')
    },
    confirmDeleteFile (file) {
      this.$confirm({
        message: 'Are you sure you want to delete this file?',
        onConfirm: close => {
          // TODO: need delete method for the backend as well
          this.$emit('input', this.valueFormated.filter(({ path }) => path !== file.path))
          close()
        },
        onCancel: close => close(),
      })
    },
    openFileDialog () {
      this.$refs.fileInput.click()
    },
    setModalState (isModalOpen) {
      this.isModalOpen = isModalOpen
    },
    toggleFilePrivacy (file) {
      const newValue = this.valueFormated.map(value => {
        if (value.path === file.path) {
          value.public = !value.public
        }
        return value
      })
      this.$emit('input', newValue)
    },
  },
}
</script>

<style>
  .file-privacy label {
    margin-right: 0.75rem
  }
</style>
